import React, { useCallback, useEffect, useState } from "react";
import LoadWrapper from "../elements/load-wrapper";
import DataProvider from "../../apis";
import CustomInput from "../form/custom-input";
import { Button, notification } from "antd";
import { useAppStore } from "../../store";
import { NotificationType } from "../../utils";
import {
  CreditCardOutlined,
  InfoCircleOutlined,
  MobileOutlined,
} from "@ant-design/icons";

const PAYMENT_GUIDELINES = [
  "Payments are done on the 3rd day of every month.",
  "If your account details change, update them at least 3 days before the payout date.",
  "Account balances are credited only for completed appointments where the report has been signed and uploaded.",
];

export default function PaymentDetails(props: { tab?: string }) {
  const [api, contextHolder] = notification.useNotification();
  const { doctor } = useAppStore();
  const { tab } = props;
  const [loading, setLoading] = useState(false);
  const [accountType, setAccountType] = useState<string>("");
  const [accountName, setAccountName] = useState<string>("");
  const [accountProvider, setAccountProvider] = useState<string>("");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [providerBranch, setProviderBranch] = useState<string>("");
  const [loadingX, setLoadingX] = useState<boolean>(false);

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const getProfile = useCallback(async () => {
    setLoading(true);
    const { data } = await DataProvider.getDoctorProfile();
    if (data.paymentDetails) {
      setAccountName(data.paymentDetails.accountName);
      setAccountNumber(data.paymentDetails.accountNumber);
      setAccountProvider(data.paymentDetails.accountProvider);
      setAccountType(data.paymentDetails.accountType);
      setProviderBranch(data.paymentDetails.providerBranch);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === "4") {
      getProfile();
    }
  }, [tab, getProfile]);

  async function onSubmit() {
    const payload = {
      paymentDetails: {
        accountType,
        accountProvider,
        providerBranch,
        accountNumber,
        accountName,
      },
    };

    try {
      if (doctor) {
        setLoadingX(true);
        await DataProvider.updateDoctorProfile(doctor.id, payload);
        setLoadingX(false);
        getProfile();
      }
    } catch (error: unknown) {
      setLoadingX(false);
      const msg =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: string }).message)
          : "Failed to update payment details.";
      openNotificationWithIcon("error", "Failed", msg);
    }
  }

  const selectBaseClass =
    "w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-slate-800 outline-none transition-all duration-200 focus:border-[#00A2F8] focus:ring-2 focus:ring-[#00A2F8]/20";

  return (
    <div
      className="min-h-[70vh] w-full rounded-2xl p-4 md:p-6"
      style={{
        background:
          "linear-gradient(160deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
      }}
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
        {/* Guidelines card — info panel */}
        <section
          className="flex flex-col rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-lg shadow-slate-200/50 backdrop-blur-sm md:p-8"
          aria-labelledby="guidelines-heading"
        >
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
              style={{
                background:
                  "linear-gradient(135deg, #00A2F8 0%, #0070C0 100%)",
              }}
              aria-hidden
            >
              <InfoCircleOutlined className="text-xl" />
            </div>
            <h2
              id="guidelines-heading"
              className="text-xl font-semibold tracking-tight text-slate-800"
            >
              Payment guidelines
            </h2>
          </div>
          <ol className="list-none space-y-4 pl-0">
            {PAYMENT_GUIDELINES.map((text, idx) => (
              <li key={idx} className="flex gap-3">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-sm font-semibold text-slate-600"
                  aria-hidden
                >
                  {idx + 1}
                </span>
                <span className="text-slate-600 leading-relaxed">{text}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Payment details form card */}
        <section
          className="flex flex-col rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-lg shadow-slate-200/50 backdrop-blur-sm md:p-8"
          aria-labelledby="payment-heading"
        >
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
              style={{
                background:
                  "linear-gradient(135deg, #00A2F8 0%, #0070C0 100%)",
              }}
              aria-hidden
            >
              <CreditCardOutlined className="text-xl" />
            </div>
            <h2
              id="payment-heading"
              className="text-xl font-semibold tracking-tight text-slate-800"
            >
              Payout details
            </h2>
          </div>

          <LoadWrapper loading={loading}>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="payment-method"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Payment method
                </label>
                <select
                  id="payment-method"
                  value={accountType}
                  onChange={(e) => {
                    setAccountType(e.target.value);
                    setAccountProvider("");
                    setAccountNumber("");
                  }}
                  className={selectBaseClass}
                  aria-label="Choose payment method"
                >
                  <option value="momo">Mobile Money</option>
                  <option value="bank">Bank</option>
                </select>
              </div>

              <CustomInput
                onChangeText={setAccountName}
                value={accountName}
                title="Account name"
                placeholder="Enter the name associated with the account"
              />

              {accountType === "momo" && (
                <div>
                  <label
                    htmlFor="momo-provider"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    Mobile money provider
                  </label>
                  <div className="relative">
                    <span
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      aria-hidden
                    >
                      <MobileOutlined />
                    </span>
                    <select
                      id="momo-provider"
                      value={accountProvider}
                      onChange={(e) => setAccountProvider(e.target.value)}
                      className={`${selectBaseClass} pl-10`}
                      aria-label="Select mobile money provider"
                    >
                      <option value="mtn">MTN</option>
                      <option value="telecel">Telecel Cash</option>
                      <option value="airteltigo">AirtelTigo</option>
                    </select>
                  </div>
                </div>
              )}

              {accountType === "bank" && (
                <>
                  <CustomInput
                    onChangeText={setAccountProvider}
                    value={accountProvider}
                    title="Bank name"
                    placeholder="Enter your bank name"
                  />
                  <CustomInput
                    onChangeText={setProviderBranch}
                    value={providerBranch}
                    title="Bank branch"
                    placeholder="Enter the bank branch"
                  />
                </>
              )}

              <CustomInput
                onChangeText={setAccountNumber}
                value={accountNumber}
                title={
                  accountType === "bank"
                    ? "Account number"
                    : "Mobile money number"
                }
                placeholder={
                  accountType === "bank"
                    ? "Enter your bank account number"
                    : "Enter your mobile money number"
                }
              />

              <div className="pt-2">
                <Button
                  type="primary"
                  size="large"
                  onClick={onSubmit}
                  loading={loadingX}
                  className="h-12 rounded-xl bg-[#00A2F8] px-8 font-semibold shadow-md transition-all hover:bg-[#0070C0] hover:shadow-lg"
                  aria-label={loadingX ? "Saving payment details" : "Save payment details"}
                >
                  {loadingX ? "Saving…" : "Save details"}
                </Button>
              </div>
            </div>
          </LoadWrapper>
        </section>
      </div>
      {contextHolder}
    </div>
  );
}
